import React, { useMemo, useState, startTransition } from 'react';
import { Tag, Space, Typography, Input, Empty } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useFilterStore } from '../../store/useFilters';
import { Ambassador } from '../../types';
import { arteliaColors } from '../../theme';

const { Text } = Typography;

interface ActivityFilterProps {
  ambassadors: Ambassador[];
}

const ActivityFilter: React.FC<ActivityFilterProps> = ({ ambassadors }) => {
  const { filters, setActivityFilter } = useFilterStore();
  const [searchTerm, setSearchTerm] = useState('');

  // Get unique activities with counts
  const activityData = useMemo(() => {
    const activityCounts = ambassadors.reduce((acc, ambassador) => {
      const activity = ambassador.activity;
      if (activity) {
        acc[activity] = (acc[activity] || 0) + 1;
      }
      return acc;
    }, {} as Record<string, number>);

    return Object.entries(activityCounts)
      .map(([activity, count]) => ({ activity, count }))
      .sort((a, b) => b.count - a.count);
  }, [ambassadors]);

  // Filter activities based on search term
  const filteredActivityData = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();
    return term
      ? activityData.filter(({ activity }) =>
          activity.toLowerCase().includes(term)
        )
      : activityData;
  }, [activityData, searchTerm]);

  const handleActivityClick = (activity: string) => {
    startTransition(() => {
      const newActivities = filters.activities.includes(activity)
        ? filters.activities.filter(a => a !== activity)
        : [...filters.activities, activity];
      setActivityFilter(newActivities);
    });
  };

  // Color mapping for different activities
  const getActivityColor = (activity: string, isSelected: boolean) => {
    if (isSelected) {
      return arteliaColors.bluePrimary;
    }
    
    // Use different colors based on activity type
    const colorMap: Record<string, string> = {
      'Environment': arteliaColors.greenDarker,
      'DEV': arteliaColors.purpleDarker,
      'Project Management': arteliaColors.redDarker,
      'Water': '#1890ff',
      'Energy': arteliaColors.yellowDarker,
      'Transport': '#722ed1',
      'Infrastructure': '#595959',
    };
    
    return colorMap[activity] || '#d9d9d9';
  };

  return (
    <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Search Input */}
      <div style={{ marginBottom: '8px', flexShrink: 0 }}>
        <Input
          placeholder="Search activities..."
          allowClear
          prefix={<SearchOutlined style={{ color: '#999' }} />}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="small"
          style={{ width: '100%' }}
        />
      </div>

      {/* Activity Tags */}
      <div style={{ 
        display: 'flex', 
        flexWrap: 'wrap', 
        gap: '6px',
        flex: 1,
        overflowY: 'auto',
        paddingRight: '4px',
        alignContent: 'flex-start',
        minHeight: 0,
      }}>
        {filteredActivityData.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {searchTerm ? `No activities found matching "${searchTerm}"` : 'No activities available'}
              </Text>
            }
            style={{ margin: '20px auto' }}
          />
        ) : (
          filteredActivityData.map(({ activity, count }) => {
          const isSelected = filters.activities.includes(activity);
          const color = getActivityColor(activity, isSelected);
          
          return (
            <Tag
              key={activity}
              color={isSelected ? undefined : 'default'}
              style={{
                cursor: 'pointer',
                margin: '2px',
                padding: '4px 8px',
                fontSize: '11px',
                borderRadius: '12px',
                backgroundColor: isSelected ? color : '#fafafa',
                borderColor: isSelected ? color : '#d9d9d9',
                color: isSelected ? 'white' : '#666',
                transition: 'all 0.2s ease',
              }}
              onClick={() => handleActivityClick(activity)}
            >
              <span style={{ marginRight: '4px' }}>{activity}</span>
              <span 
                style={{
                  fontSize: '9px',
                  backgroundColor: isSelected ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.1)',
                  padding: '1px 4px',
                  borderRadius: '6px',
                }}
              >
                {count}
              </span>
            </Tag>
          );
        })
        )}
      </div>
      
      {filters.activities.length > 0 && (
        <div style={{ marginTop: '8px', fontSize: '11px', color: '#666', flexShrink: 0 }}>
          <Text type="secondary">
            {filters.activities.length} activit{filters.activities.length > 1 ? 'ies' : 'y'} selected
          </Text>
        </div>
      )}
    </div>
  );
};

export default ActivityFilter;